import React from 'react';
import styled from 'styled-components';

const ToolbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #333;
  color: white;
  width: 60px; /* Increased width to accommodate larger icons */
  padding: 10px 0;
`;

const ToolButton = styled.button`
  background-color: ${props => props.active ? '#555' : 'transparent'};
  color: white;
  border: none;
  padding: 12px 0; /* Increased padding for larger touch targets */
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #444;
  }
  
  svg {
    width: 28px; /* Increased icon size */
    height: 28px; /* Increased icon size */
    margin-bottom: 5px;
  }
  
  span {
    font-size: 10px;
    text-align: center;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #555;
  margin: 8px 10px;
`;

const Toolbar = ({ svgCode, setSvgCode }) => {
  const handleRotate = (degrees) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgCode, 'image/svg+xml');
    const svgElement = doc.querySelector('svg');
    
    if (svgElement) {
      const currentTransform = svgElement.getAttribute('transform') || '';
      svgElement.setAttribute('transform', `${currentTransform} rotate(${degrees})`);
      setSvgCode(new XMLSerializer().serializeToString(doc));
    }
  };

  const handleFlipX = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgCode, 'image/svg+xml');
    const svgElement = doc.querySelector('svg');
    
    if (svgElement) {
      const currentTransform = svgElement.getAttribute('transform') || '';
      svgElement.setAttribute('transform', `${currentTransform} scale(-1, 1)`);
      setSvgCode(new XMLSerializer().serializeToString(doc));
    }
  };

  const handleFlipY = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgCode, 'image/svg+xml');
    const svgElement = doc.querySelector('svg');
    
    if (svgElement) {
      const currentTransform = svgElement.getAttribute('transform') || '';
      svgElement.setAttribute('transform', `${currentTransform} scale(1, -1)`);
      setSvgCode(new XMLSerializer().serializeToString(doc));
    }
  };

  const handleOptimize = () => {
    // Basic optimization (removing whitespace)
    const optimized = svgCode
      .replace(/>\s+</g, '><')
      .replace(/\s+/g, ' ')
      .trim();
    setSvgCode(optimized);
  };

  const handleClear = () => {
    setSvgCode('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 124 124" fill="none"><rect width="124" height="124" rx="24" fill="#F97316"/></svg>');
  };
  
  const handleRemoveTransforms = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgCode, 'image/svg+xml');
    const svgElement = doc.querySelector('svg');
    
    if (svgElement) {
      svgElement.removeAttribute('transform');
      setSvgCode(new XMLSerializer().serializeToString(doc));
    }
  };
  
  const handleAddRectangle = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgCode, 'image/svg+xml');
      const svg = doc.querySelector('svg');
      
      if (svg) {
        // Get SVG dimensions
        const width = parseInt(svg.getAttribute('width') || 100);
        const height = parseInt(svg.getAttribute('height') || 100);
        
        // Create a rectangle element
        const rect = doc.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', width / 4);
        rect.setAttribute('y', height / 4);
        rect.setAttribute('width', width / 2);
        rect.setAttribute('height', height / 2);
        rect.setAttribute('fill', '#3498db');
        
        // Append the rectangle to the SVG
        svg.appendChild(rect);
        
        // Convert back to string
        const serializer = new XMLSerializer();
        const newSvgCode = serializer.serializeToString(doc);
        
        setSvgCode(newSvgCode);
      }
    } catch (e) {
      console.error('Error adding rectangle:', e);
    }
  };
  
  const handleAddCircle = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgCode, 'image/svg+xml');
      const svg = doc.querySelector('svg');
      
      if (svg) {
        // Get SVG dimensions
        const width = parseInt(svg.getAttribute('width') || 100);
        const height = parseInt(svg.getAttribute('height') || 100);
        
        // Create a circle element
        const circle = doc.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', width / 2);
        circle.setAttribute('cy', height / 2);
        circle.setAttribute('r', Math.min(width, height) / 4);
        circle.setAttribute('fill', '#e74c3c');
        
        // Append the circle to the SVG
        svg.appendChild(circle);
        
        // Convert back to string
        const serializer = new XMLSerializer();
        const newSvgCode = serializer.serializeToString(doc);
        
        setSvgCode(newSvgCode);
      }
    } catch (e) {
      console.error('Error adding circle:', e);
    }
  };
  
  const handleAddText = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgCode, 'image/svg+xml');
      const svg = doc.querySelector('svg');
      
      if (svg) {
        // Get SVG dimensions
        const width = parseInt(svg.getAttribute('width') || 100);
        const height = parseInt(svg.getAttribute('height') || 100);
        
        // Create a text element
        const text = doc.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', width / 2);
        text.setAttribute('y', height / 2);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('dominant-baseline', 'middle');
        text.setAttribute('font-family', 'Arial');
        text.setAttribute('font-size', '20');
        text.setAttribute('fill', '#333');
        text.textContent = 'Text';
        
        // Append the text to the SVG
        svg.appendChild(text);
        
        // Convert back to string
        const serializer = new XMLSerializer();
        const newSvgCode = serializer.serializeToString(doc);
        
        setSvgCode(newSvgCode);
      }
    } catch (e) {
      console.error('Error adding text:', e);
    }
  };
  
  return (
    <ToolbarContainer>
      <ToolButton onClick={() => handleRotate(90)} title="Rotate 90°">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.707 11.293l-4-4a1 1 0 00-1.414 1.414L13.586 12l-3.293 3.293a1 1 0 101.414 1.414l4-4a1 1 0 000-1.414z" />
          <path d="M10.293 11.293l-4-4a1 1 0 00-1.414 1.414L8.586 12l-3.293 3.293a1 1 0 101.414 1.414l4-4a1 1 0 000-1.414z" />
        </svg>
        <span>Rotate 90°</span>
      </ToolButton>
      
      <ToolButton onClick={() => handleRotate(-90)} title="Rotate -90°">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.707 11.293l-4-4a1 1 0 00-1.414 1.414L13.586 12l-3.293 3.293a1 1 0 101.414 1.414l4-4a1 1 0 000-1.414z" />
          <path d="M10.293 11.293l-4-4a1 1 0 00-1.414 1.414L8.586 12l-3.293 3.293a1 1 0 101.414 1.414l4-4a1 1 0 000-1.414z" />
        </svg>
        <span>Rotate -90°</span>
      </ToolButton>
      
      <ToolButton onClick={handleFlipX} title="Flip X">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.707 11.293l-4-4a1 1 0 00-1.414 1.414L13.586 12l-3.293 3.293a1 1 0 101.414 1.414l4-4a1 1 0 000-1.414z" />
          <path d="M10.293 11.293l-4-4a1 1 0 00-1.414 1.414L8.586 12l-3.293 3.293a1 1 0 101.414 1.414l4-4a1 1 0 000-1.414z" />
        </svg>
        <span>Flip X</span>
      </ToolButton>
      
      <ToolButton onClick={handleFlipY} title="Flip Y">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.707 11.293l-4-4a1 1 0 00-1.414 1.414L13.586 12l-3.293 3.293a1 1 0 101.414 1.414l4-4a1 1 0 000-1.414z" />
          <path d="M10.293 11.293l-4-4a1 1 0 00-1.414 1.414L8.586 12l-3.293 3.293a1 1 0 101.414 1.414l4-4a1 1 0 000-1.414z" />
        </svg>
        <span>Flip Y</span>
      </ToolButton>
      
      <ToolButton onClick={handleRemoveTransforms} title="Reset Transforms">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
          <path d="M12 10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
        </svg>
        <span>Reset Transforms</span>
      </ToolButton>
      
      <Divider />
      
      <ToolButton onClick={handleAddRectangle} title="Add Rectangle">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
        <span>Rectangle</span>
      </ToolButton>
      
      <ToolButton onClick={handleAddCircle} title="Add Circle">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="9" />
        </svg>
        <span>Circle</span>
      </ToolButton>
      
      <ToolButton onClick={handleAddText} title="Add Text">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M5 5v2h14V5H5zm2 4v10h2V9H7zm8 0v10h2V9h-2zm-4 3v7h2v-7h-2z" />
        </svg>
        <span>Text</span>
      </ToolButton>
      
      <Divider />
      
      <ToolButton onClick={handleOptimize} title="Optimize">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
          <path d="M12 10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
        </svg>
        <span>Optimize</span>
      </ToolButton>
      
      <ToolButton onClick={handleClear} title="Clear">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
          <path d="M12 10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
        </svg>
        <span>Clear</span>
      </ToolButton>
    </ToolbarContainer>
  );
};

export default Toolbar; 